import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { Result } from 'src_common/common/mobx-utils/Form2/FormModel';
import { DateTime } from 'src_common/utils/time/time';
import { validateNotEmpty } from 'src_common/common/mobx-utils/Form2/validators';
import { TotalBetViewType } from 'src/api_openapi/v2/bonus-engine/schemas';

const cleanFloatNumberString = (s: string): string => s.replaceAll(',', '.');

export const parseNumeric = (numeric: string): number => {
    const cleanNumeric = cleanFloatNumberString(numeric);
    if (/^\d+(\.\d+)?$/.test(cleanNumeric.trim())) {
        return parseFloat(cleanNumeric);
    }
    return NaN;
};

export const validateIf = <T>(condition: () => boolean, validator: () => Result<T>): Result<T | string> => {
    if (condition()) {
        return validator();
    }
    return Result.createOk('');
};

/**
 * Parse numeric string to number
 * @param optional - if true, empty string is valid
 * @param message - error message
 * @param value - array parsed to string
 */
export const isMultiselectNotEmpty =
    (optional: boolean, message: string) =>
    (value: string): Result<string> => {
        if (optional === true && (value === '' || value === '[]')) {
            return Result.createOk(value);
        }

        try {
            const inputArray: string[] = JSON.parse(value);
            if (Array.isArray(inputArray) && inputArray.length > 0) {
                return Result.createOk(value);
            }
            return Result.createError(message);
        } catch (e) {
            return Result.createError(message);
        }
    };

export const isNumeric =
    (optional: boolean, message: string) =>
    (value: string): Result<string> => {
        if (optional === true && value === '') {
            return Result.createOk(value);
        }

        const numberValue = parseNumeric(value);
        if (isNaN(numberValue)) {
            return Result.createError(message);
        }
        return Result.createOk(value);
    };

export const isNumericWholeNumber =
    (optional: boolean, message: string) =>
    (value: string): Result<string> => {
        if (optional === true && value === '') {
            return Result.createOk(value);
        }

        const numberValue = parseNumeric(value);
        if (value.includes('.') || value.includes(',') || isNaN(numberValue) || numberValue === 0) {
            return Result.createError(message);
        }

        return Result.createOk(value);
    };

export const isNumericPrecission =
    (precission: number, optional: boolean, message: string) =>
    (value: string): Result<string> => {
        if (optional === true && value === '') {
            return Result.createOk(value);
        }

        const numberValue = parseNumeric(value);
        const splitNumber = numberValue.toString().split('.');
        if (splitNumber.length === 1) {
            return Result.createOk(value);
        }
        const decimalPart = splitNumber[1]?.length ?? 0;
        if (decimalPart > precission || isNaN(numberValue)) {
            return Result.createError(message);
        }
        return Result.createOk(value);
    };

export const isNumericRelationWithNumber =
    (threshold: number, type: 'more' | 'less', inclusive: boolean, requiredIf: () => boolean, message: string) =>
    (value: string): Result<string> => {
        const valueNumber = parseNumeric(value);
        const required = requiredIf();
        if (required === false && value === '') {
            return Result.createOk(value);
        }

        if (isNaN(valueNumber)) {
            return Result.createError(message);
        }

        const isValid =
            type === 'more'
                ? inclusive
                    ? valueNumber >= threshold
                    : valueNumber > threshold
                : inclusive
                  ? valueNumber <= threshold
                  : valueNumber < threshold;

        return isValid ? Result.createOk(value) : Result.createError(message);
    };

export const countDecimalPlaces = (num: number): number => {
    const numStr = num.toString();
    const decimalIndex = numStr.indexOf('.');

    return decimalIndex >= 0 ? numStr.length - decimalIndex - 1 : 0;
};

export const valueToAmountView = (num: number, currency?: string): number | string => {
    if (typeof currency === 'undefined') return num;

    const amount = countDecimalPlaces(num) === 0 ? num.toFixed(2) : num;

    return `${currency}${amount}`;
};

export const isNumericBetween =
    ({
        from,
        to,
        inclusive = true,
        optional = false,
        currency,
        message,
    }: {
        from: number;
        to: number;
        inclusive?: boolean;
        optional?: boolean;
        currency?: string;
        message?: string;
    }) =>
    (value: string): Result<string> => {
        let errorMessage;

        if (typeof message === 'string') {
            errorMessage = message;
        } else {
            errorMessage = `Enter a number. Between ${valueToAmountView(from, currency)} and ${valueToAmountView(to, currency)}`;
        }

        if (optional && value === '') {
            return Result.createOk(value);
        }

        const numberValue = parseNumeric(value);

        if (isNaN(numberValue)) {
            return Result.createError(errorMessage);
        }

        const isValid = inclusive ? numberValue >= from && numberValue <= to : numberValue > from && numberValue < to;

        return isValid ? Result.createOk(value) : Result.createError(errorMessage);
    };

export const isNumericRelationWithInput =
    (
        getNumberInput: () => FormInputState<string, string>,
        type: 'less' | 'more',
        inclusive: boolean,
        optional: boolean,
        message: string
    ) =>
    (value: string): Result<string> => {
        if (optional && value === '') {
            return Result.createOk(value);
        }
        const numberInput = getNumberInput();
        const threshold = parseNumeric(numberInput.value);
        const valueNumber = parseNumeric(value);

        if (isNaN(threshold)) {
            // when referenced input is not filled this input should not throw value relation error
            return Result.createOk(value);
        }

        if (isNaN(valueNumber)) {
            return Result.createError(message);
        }

        const isValid =
            type === 'more'
                ? inclusive
                    ? valueNumber >= threshold
                    : valueNumber > threshold
                : inclusive
                  ? valueNumber <= threshold
                  : valueNumber < threshold;

        return isValid ? Result.createOk(value) : Result.createError(message);
    };

export const isDateAfter =
    (getDateBeforeInput: () => string, inclusive: boolean, message: string) =>
    (value: string): Result<string> => {
        const currentDate = DateTime.from(value);
        const dateBeforeInput = getDateBeforeInput();
        const beforeDate = DateTime.from(dateBeforeInput);
        if (currentDate === null || beforeDate === null) {
            return Result.createError(message);
        }
        if (inclusive && currentDate.isSame(beforeDate)) {
            return Result.createOk(value);
        }
        return currentDate.isAfter(beforeDate) ? Result.createOk(value) : Result.createError(message);
    };

export const isDateBefore =
    (getDateAfterInput: () => string, inclusive: boolean, message: string) =>
    (value: string): Result<string> => {
        const currentDate = DateTime.from(value);
        const dateAfterInput = getDateAfterInput();
        const afterDate = DateTime.from(dateAfterInput);
        if (currentDate === null || afterDate === null) {
            return Result.createError(message);
        }
        if (inclusive && currentDate.isSame(afterDate)) {
            return Result.createOk(value);
        }
        return currentDate.isBefore(afterDate) ? Result.createOk(value) : Result.createError(message);
    };

export const isDateAfterIf =
    (getDateBeforeInput: () => string, inclusive: boolean, message: string, requiredIf: () => boolean) =>
    (value: string): Result<string> => {
        if (requiredIf() === false) {
            return Result.createOk(value);
        }
        const currentDate = DateTime.from(value);
        const dateBeforeInput = getDateBeforeInput();
        const beforeDate = DateTime.from(dateBeforeInput);
        if (currentDate === null || beforeDate === null) {
            return Result.createError(message);
        }
        if (inclusive && currentDate.isSame(beforeDate)) {
            return Result.createOk(value);
        }
        return currentDate.isAfter(beforeDate) ? Result.createOk(value) : Result.createError(message);
    };

export const isStringLength =
    (type: 'more' | 'less', length: number, inclusive: boolean, optional: boolean, message: string) =>
    (value: string): Result<string> => {
        if (optional && value === '') {
            return Result.createOk(value);
        }
        const isMore = type === 'more';
        const result = inclusive
            ? isMore
                ? value.length >= length
                : value.length <= length
            : isMore
              ? value.length > length
              : value.length < length;

        if (result === false) {
            return Result.createError(message);
        }
        return Result.createOk(value);
    };

/**
 * Validates relative expiry days based on the selected expiry type.
 * If expiry type is 'relative-expiry', validates that the input is a whole number greater than 0.
 * If expiry type is not 'relative-expiry', the validation is skipped.
 *
 * @param getExpiryType - Function that returns the current expiry type
 * @returns A validation function
 */
export const validateRelativeExpiryDays =
    (getExpiryType: () => string) =>
    (value: string): Result<string> => {
        const expiryType = getExpiryType();
        if (expiryType !== 'relative-expiry') {
            return Result.createOk(value);
        }

        const notEmptyResult = validateNotEmpty('Enter a number greater than 0')(value);
        if (notEmptyResult.value.type === 'error') {
            return notEmptyResult;
        }

        const wholeNumberResult = isNumericWholeNumber(false, 'Enter a whole number')(value);
        if (wholeNumberResult.value.type === 'error') {
            return wholeNumberResult;
        }

        return isNumericRelationWithNumber(0, 'more', false, () => false, 'Enter a number greater than 0')(value);
    };

export const validateCsvIsRequired =
    (message: string) =>
    (value: number[]): Result<number[]> =>
        value.length === 0 ? Result.createError(message) : Result.createOk(value);

/**
 * @deprecated - Please use this method "validateIf"
 */
export const validateEmptyIf =
    (message: string, requiredIf: () => boolean) =>
    (value: string | undefined): Result<string> => {
        const required = requiredIf();
        if (required) {
            return value === '' || value === undefined ? Result.createOk(value ?? '') : Result.createError(message);
        }
        return Result.createOk(value ?? '');
    };

/**
 * @deprecated - Please use this method "validateIf"
 */
export const validateNotEmptyIf =
    (message: string, requiredIf: () => boolean) =>
    (value: string | undefined): Result<string> => {
        const required = requiredIf();
        if (required) {
            return value === '' || value === undefined ? Result.createError(message) : Result.createOk(value);
        }
        return Result.createOk(value ?? '');
    };

export const validIsUnique =
    <T>(getValues: () => Array<T>) =>
    (value: T): Result<T> => {
        const values = getValues();

        if (values.includes(value)) {
            return Result.createError(`The value should be unique`);
        }
        return Result.createOk(value);
    };

export const validatePickerNotEmpty =
    (message: string) =>
    (value: string | undefined): Result<string> =>
        value === '' || value === undefined || value === '[]' ? Result.createError(message) : Result.createOk(value);

export type TotalBetPossibleValuesStatusType =
    | { type: 'idle' }
    | { type: 'loading' }
    | { type: 'ready'; value: TotalBetViewType }
    | { type: 'error'; message: string };

const toCents = (value: number): number => Math.round(value * 100);

const formatTotalBet = (value: number): string => value.toFixed(2);

/**
 * Validates total bet against values supported by the selected free spins games.
 * Loading and idle states are treated as valid - the result is re-evaluated once the response arrives.
 *
 * @param getStatus - Function that returns the current total bet possible values status
 * @param getGameLabel - Function that maps game id to a game name used in error messages
 */
export const validateTotalBetPossibleValues =
    (getStatus: () => TotalBetPossibleValuesStatusType, getGameLabel: (gameId: string) => string) =>
    (value: string): Result<string> => {
        const status = getStatus();

        if (status.type === 'idle' || status.type === 'loading') {
            return Result.createOk(value);
        }

        if (status.type === 'error') {
            return Result.createError('Total bet could not be verified for the selected games. Try again later.');
        }

        const totalBet = parseNumeric(value);

        if (isNaN(totalBet)) {
            return Result.createOk(value);
        }

        const possibleValues = status.value.possibleValues ?? [];
        const missingByValue = status.value.missingByValue ?? [];

        if (possibleValues.length === 0) {
            return Result.createError('The selected games have no available total bet values.');
        }

        const toGameNames = (gameIds: Array<string>): string =>
            gameIds
                .map((gameId) => {
                    const label = getGameLabel(gameId);
                    return label === '' ? gameId : label;
                })
                .join(', ');

        const totalBetCents = toCents(totalBet);
        const isPossibleValue = possibleValues.some((possibleValue) => toCents(possibleValue) === totalBetCents);

        if (isPossibleValue) {
            const gamesToDeselect =
                missingByValue.find((missing) => toCents(missing.value) === totalBetCents)?.gameIds ?? [];

            return gamesToDeselect.length === 0
                ? Result.createOk(value)
                : Result.createError(
                      `Total bet ${formatTotalBet(totalBet)} is not available for: ${toGameNames(gamesToDeselect)}. Deselect them to use this value.`
                  );
        }

        const availableValues = possibleValues.map(formatTotalBet).join(', ');
        const gamesToDeselect = [...new Set(missingByValue.flatMap((missing) => missing.gameIds ?? []))];

        return gamesToDeselect.length === 0
            ? Result.createError(`Available total bet values: ${availableValues}.`)
            : Result.createError(
                  `Available total bet values: ${availableValues}, after deselecting: ${toGameNames(gamesToDeselect)}.`
              );
    };
